import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Supported languages with flags
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸', locale: 'en-US' },
  { code: 'es', name: 'Español', flag: '🇪🇸', locale: 'es-ES' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', locale: 'it-IT' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', locale: 'de-DE' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', locale: 'fr-FR' },
];

// Amazon domains per region
export const AMAZON_DOMAINS = {
  en: 'amazon.com',
  es: 'amazon.es',
  it: 'amazon.it',
  de: 'amazon.de',
  fr: 'amazon.fr',
};

// Translation strings - Natural, not templated
const TRANSLATIONS = {
  en: {
    // Navigation
    home: 'Home',
    blog: 'Blog',
    about: 'About',
    contact: 'Contact',
    categories: 'Categories',
    
    // CTAs - Natural language
    check_price: 'Check Price',
    check_price_amazon: 'Amazon',
    view_deal: 'View Deal',
    see_offer: 'See Offer',
    read_more: 'Read Reviews',
    read_article: 'Read Full Article',
    learn_more: 'Learn More',
    coming_soon: 'Coming Soon',
    view_all: 'View All',
    
    // Newsletter
    newsletter: 'Newsletter',
    get_best_gear: 'Get the Best Gear Picks',
    weekly_reviews: 'Weekly reviews, deals, and tips for digital nomads. No spam, unsubscribe anytime.',
    your_name: 'Your name',
    your_email: 'Your email',
    privacy_agree: 'I agree to receive emails and accept the Privacy Policy. You can unsubscribe at any time.',
    subscribe_now: 'Subscribe',
    subscribing: 'Subscribing...',
    join_nomads: 'Join 5,000+ digital nomads',
    youre_in: "You're In!",
    check_inbox: 'Check your inbox for our welcome guide.',
    weekly_gear_picks: 'Get Weekly Gear Picks',
    
    // Home sections
    stay_connected: 'Stay Connected Anywhere',
    connectivity_desc: 'Reliable internet is non-negotiable for remote work. These are the tools that keep us online everywhere.',
    mobile_routers_5g: '5G/4G Mobile Routers',
    signal_boosters: 'Signal Boosters & Antennas',
    esim_solutions: 'eSIM Solutions',
    starlink_satellite: 'Starlink & Satellite',
    best_mobile_routers: 'Best Mobile Routers',
    power_your_work: 'Power Your Work Anywhere',
    power_desc: 'From portable power stations to solar panels, stay powered in any environment.',
    power_stations: 'Portable Power Stations',
    solar_charging: 'Solar Charging',
    laptop_power_banks: 'Laptop Power Banks',
    travel_adapters: 'Travel Adapters',
    best_power_stations: 'Best Power Stations',
    
    // Product labels
    best_overall: 'Best Overall',
    best_budget: 'Best Budget',
    best_premium: 'Best Premium',
    best_compact: 'Best Compact',
    editors_choice: "Editor's Choice",
    top_pick: 'Top Pick',
    
    // Section titles
    top_rated_products: 'Top Rated Products',
    new_arrivals: 'New Arrivals',
    new_in_2026: 'NEW IN 2026',
    latest_reviews: 'Latest Reviews',
    featured_products: 'Featured Products',
    popular_categories: 'Popular Categories',
    related_products: 'Related Products',
    you_may_also_like: 'You May Also Like',
    latest_additions: 'Latest additions to our tested gear collection.',
    view_all_new: 'View All New Products',
    in_depth_guides: 'In-depth guides and product comparisons.',
    editors_picks: "Our editors' picks for the best gear this month.",
    view_all_reviews: 'View All Reviews',
    
    // Category names
    portable_power: 'Portable Power',
    travel_monitors: 'Travel Monitors',
    mobile_routers: 'Mobile Routers',
    solar_panels: 'Solar Panels',
    power_banks: 'Power Banks',
    headphones: 'Headphones',
    keyboards: 'Keyboards',
    webcams: 'Webcams',
    connectivity: 'Connectivity',
    workstations: 'Workstations',
    displays: 'Displays',
    power: 'Power',
    accessories: 'Accessories',
    guides: 'Guides',
    
    // Disclaimers
    affiliate_disclosure: 'As an Amazon Associate, we earn from qualifying purchases.',
    price_disclaimer: 'Prices may vary. Last verified',
    
    // Common
    search: 'Search',
    subscribe: 'Subscribe',
    send: 'Send',
    cancel: 'Cancel',
    select_language: 'Select Language',
    no_products_found: 'No products found',
    loading: 'Loading...',
    
    // Category pages
    buying_guide: 'Buying Guide',
    what_to_consider: 'What to Consider',
    faq: 'FAQ',
    related_articles: 'Related Articles',
    all_articles: 'All Articles',
    
    // Article
    share: 'Share',
    bookmark: 'Bookmark',
    read_time: 'min read',
    quick_pick: 'Quick Pick',
    which_one_right: 'Which One Is Right For You?',
    
    // Hero section
    hero_independently_tested: 'Independently Tested',
    hero_readers: '5,000+ readers',
    hero_rating: '4.9 rating',
    hero_best_power_stations: 'Best Power Stations',
    hero_description: 'We test and recommend the best tech tools for remote work, off-grid living, and digital nomad life. Real-world reviews by real nomads.',
    hero_latest_review: 'Latest Review',
    hero_title: 'Work From Anywhere Without Limits',
    hero_editorial_title: 'Your Trusted Guide to the Best',
    hero_editorial_title_accent: 'Remote Work Gear',
    hero_editorial_subtitle: 'Honest, in-depth reviews of laptops, power stations, routers, and everything a digital nomad needs — written by people who actually use this stuff.',
    hero_cta_reviews: 'Read Our Latest Reviews',
    hero_featured_review: 'Featured Review',
    hero_read_review: 'Read Review',
    
    // Price disclaimer
    price_disclaimer: 'Price accurate at time of writing. Check latest price on Amazon.',
    
    // Homepage categories
    cat_portable_power: 'Portable Power',
    cat_portable_power_desc: 'Solar-ready power stations',
    cat_travel_monitors: 'Travel Monitors',
    cat_travel_monitors_desc: 'Portable dual-screen setups',
    cat_mobile_routers: 'Mobile Routers',
    cat_mobile_routers_desc: '4G/5G connectivity anywhere',
    cat_remote_work: 'Remote Work Setup',
    cat_remote_work_desc: 'Mobile office essentials',
    cat_vanlife: 'Vanlife Tech',
    cat_vanlife_desc: 'Tech for life on the road',
    cat_connectivity_tools: 'Connectivity',
    cat_connectivity_tools_desc: 'Stay connected worldwide',
    
    // Categories section
    best_tools_title: 'Best Tools for Digital Nomads',
    best_tools_desc: 'Curated gear recommendations across six essential categories for remote work and travel.',
    latest_reviews_guides: 'Latest Reviews & Guides',
    view_all_articles: 'View All Articles',
    editors_picks_title: "Editor's Picks",
    editors_picks_subtitle: 'Our top-rated gear, tested and reviewed',
    view_all_products: 'View All Products',
    explore_by_topic: 'Explore by Topic',
    explore_by_topic_desc: 'Browse our coverage by category — from portable power to remote work setups',
    featured_guide_label: 'In-Depth Guide',
    featured_guide_cta: 'Read the Full Guide',
    
    // Article sections
    the_challenge: 'The Challenge',
    the_solution: 'The Solution',
    related_guides: 'Related Guides',
    key_features: 'Key Features for Remote Work',
    pros_and_cons: 'Pros and Cons',
    who_is_for: 'Who This Is For',
    best_alternatives: 'Best Alternatives on Amazon',
    our_verdict: 'Our Verdict',
    faqs: 'Frequently Asked Questions',
    what_to_consider: 'What to Consider',
    how_we_tested: 'How We Tested',
    personally_tested: 'Personally Tested',
    top_products: 'Top Products',
    in_depth_guides: 'In-Depth Guides',
    all_articles: 'All Articles',
    buying_guide: 'Buying Guide',
    hero_subtitle: 'Expert reviews and guides for the gear that powers your remote lifestyle.',
    
    // Navigation menu items
    nav_workstations: 'Workstations',
    nav_connectivity: 'Connectivity',
    nav_displays: 'Displays',
    nav_power: 'Power',
    nav_travel_setup: 'Travel Setup',
    nav_accessories: 'Accessories',
    nav_guides: 'Guides',
    
    // Workstation submenu
    nav_temporary_desk: 'Temporary Desk Setup',
    nav_hotel_room: 'Hotel Room Setup',
    nav_compact_workstations: 'Compact Workstations',
    nav_shared_spaces: 'Shared Spaces',
    nav_on_the_move: 'On-the-Move Setup',
    nav_dual_monitor: 'Dual Monitor Laptop Setup',
    nav_standing_flexible: 'Standing & Flexible Workstations',
    nav_workspace_config: 'Workspace Configurations',
    
    // Language selector
    active: 'Active',
    
    // Travel setup - already exists as travel_setup but adding more
    travel_setup: 'Travel Setup',
    
    // Footer
    footer_equipment: 'Equipment',
    footer_workstations: 'Workstations',
    footer_setup_guides: 'Setup Guides',
    footer_resources: 'Resources',
    footer_all_guides: 'All Guides',
    footer_about_us: 'About Us',
    footer_independently_tested: 'Independently Tested',
    footer_affiliate_disclosure: 'Affiliate Disclosure',
    footer_affiliate_text: 'Prices shown are accurate at time of writing. Please check current prices on Amazon before purchasing. We only recommend products we have tested and believe in.',
    footer_all_rights: 'All rights reserved.',
    footer_privacy_policy: 'Privacy Policy',
    footer_terms: 'Terms of Service',
    footer_cookies: 'Cookies',
    footer_description: 'Equipment reviews and workspace configurations for productive work from any location.',
    
    // SEO categories
    seo_laptop_setup: 'Laptop Setup',
    seo_temporary_workspaces: 'Temporary Workspaces',
    seo_dual_monitor: 'Dual Monitor Setup',
    seo_portable_office: 'Portable Office',
    seo_work_from_hotel: 'Work From Hotel',
    seo_mobile_connectivity: 'Mobile Connectivity',
  },
  
  es: {
    home: 'Inicio',
    blog: 'Blog',
    about: 'Sobre Nosotros',
    contact: 'Contacto',
    categories: 'Categorías',
    
    check_price: 'Ver Precio',
    check_price_amazon: 'Ver en Amazon',
    view_deal: 'Ver Oferta',
    see_offer: 'Ver Oferta',
    read_more: 'Leer Reseñas',
    read_article: 'Leer Artículo Completo',
    learn_more: 'Más Información',
    coming_soon: 'Próximamente',
    view_all: 'Ver Todo',
    
    // Newsletter
    newsletter: 'Boletín',
    get_best_gear: 'Recibe las Mejores Selecciones',
    weekly_reviews: 'Reseñas semanales, ofertas y consejos para nómadas digitales. Sin spam.',
    your_name: 'Tu nombre',
    your_email: 'Tu email',
    privacy_agree: 'Acepto recibir emails y la Política de Privacidad. Puedes darte de baja en cualquier momento.',
    subscribe_now: 'Suscribirse',
    subscribing: 'Suscribiendo...',
    join_nomads: 'Únete a 5.000+ nómadas digitales',
    youre_in: '¡Estás Dentro!',
    check_inbox: 'Revisa tu bandeja para la guía de bienvenida.',
    weekly_gear_picks: 'Recibe Consejos Semanales',
    
    // Home sections
    stay_connected: 'Mantente Conectado en Cualquier Lugar',
    connectivity_desc: 'Internet confiable es esencial para el trabajo remoto. Estas son las herramientas que nos mantienen online.',
    mobile_routers_5g: 'Routers Móviles 5G/4G',
    signal_boosters: 'Amplificadores de Señal',
    esim_solutions: 'Soluciones eSIM',
    starlink_satellite: 'Starlink y Satélite',
    best_mobile_routers: 'Mejores Routers Móviles',
    power_your_work: 'Alimenta Tu Trabajo en Cualquier Lugar',
    power_desc: 'Desde estaciones de energía portátiles hasta paneles solares, mantente alimentado en cualquier entorno.',
    power_stations: 'Estaciones de Energía Portátiles',
    solar_charging: 'Carga Solar',
    laptop_power_banks: 'Power Banks para Laptop',
    travel_adapters: 'Adaptadores de Viaje',
    best_power_stations: 'Mejores Estaciones de Energía',
    
    best_overall: 'Mejor Opción',
    best_budget: 'Mejor Precio',
    best_premium: 'Mejor Premium',
    best_compact: 'Más Compacto',
    editors_choice: 'Selección del Editor',
    top_pick: 'Favorito',
    
    top_rated_products: 'Productos Mejor Valorados',
    new_arrivals: 'Novedades',
    new_in_2026: 'NUEVO EN 2026',
    latest_reviews: 'Últimas Reseñas',
    featured_products: 'Productos Destacados',
    popular_categories: 'Categorías Populares',
    related_products: 'Productos Relacionados',
    you_may_also_like: 'También Te Puede Gustar',
    latest_additions: 'Últimas adiciones a nuestra colección de equipo probado.',
    view_all_new: 'Ver Todos los Nuevos Productos',
    in_depth_guides: 'Guías detalladas y comparaciones de productos.',
    editors_picks: 'Las selecciones de nuestros editores para el mejor equipo de este mes.',
    view_all_reviews: 'Ver Todas las Reseñas',
    
    portable_power: 'Energía Portátil',
    travel_monitors: 'Monitores de Viaje',
    mobile_routers: 'Routers Móviles',
    solar_panels: 'Paneles Solares',
    power_banks: 'Baterías Externas',
    headphones: 'Auriculares',
    keyboards: 'Teclados',
    webcams: 'Webcams',
    connectivity: 'Conectividad',
    workstations: 'Estaciones de Trabajo',
    displays: 'Pantallas',
    power: 'Energía',
    accessories: 'Accesorios',
    guides: 'Guías',
    
    affiliate_disclosure: 'Como Afiliado de Amazon, ganamos por compras elegibles.',
    price_disclaimer: 'Los precios pueden variar. Última verificación',
    
    search: 'Buscar',
    subscribe: 'Suscribirse',
    send: 'Enviar',
    cancel: 'Cancelar',
    select_language: 'Seleccionar Idioma',
    no_products_found: 'No se encontraron productos',
    loading: 'Cargando...',
    
    buying_guide: 'Guía de Compra',
    what_to_consider: 'Qué Considerar',
    faq: 'Preguntas Frecuentes',
    related_articles: 'Artículos Relacionados',
    all_articles: 'Todos los Artículos',
    
    share: 'Compartir',
    bookmark: 'Guardar',
    read_time: 'min de lectura',
    quick_pick: 'Elección Rápida',
    which_one_right: '¿Cuál es el Adecuado Para Ti?',
    
    hero_title: 'Trabaja Desde Cualquier Lugar Sin Límites',
    hero_subtitle: 'Reseñas y guías expertas para el equipo que impulsa tu estilo de vida remoto.',
    hero_independently_tested: 'Probado Independientemente',
    hero_readers: '5.000+ lectores',
    hero_rating: '4.9 valoración',
    hero_best_power_stations: 'Mejores Estaciones de Energía',
    hero_description: 'Probamos y recomendamos las mejores herramientas tecnológicas para trabajo remoto, vida off-grid y nómadas digitales. Reseñas reales por nómadas reales.',
    hero_latest_review: 'Última Reseña',
    hero_editorial_title: 'Tu Guía de Confianza para el Mejor',
    hero_editorial_title_accent: 'Equipo de Trabajo Remoto',
    hero_editorial_subtitle: 'Reseñas honestas y detalladas de portátiles, estaciones de energía, routers y todo lo que un nómada digital necesita.',
    hero_cta_reviews: 'Lee Nuestras Últimas Reseñas',
    hero_featured_review: 'Reseña Destacada',
    hero_read_review: 'Leer Reseña',
    latest_reviews_guides: 'Últimas Reseñas y Guías',
    view_all_articles: 'Ver Todos los Artículos',
    editors_picks_title: 'Selección del Editor',
    editors_picks_subtitle: 'Nuestro equipo mejor valorado, probado y revisado',
    view_all_products: 'Ver Todos los Productos',
    explore_by_topic: 'Explorar por Tema',
    explore_by_topic_desc: 'Navega por nuestras categorías — desde energía portátil hasta setups de trabajo remoto',
    featured_guide_label: 'Guía Detallada',
    featured_guide_cta: 'Leer la Guía Completa',

    // Price disclaimer
    price_disclaimer: 'Precio exacto al momento de escribir. Consulta el precio actual en Amazon.',
    
    // Homepage categories
    cat_portable_power: 'Energía Portátil',
    cat_portable_power_desc: 'Estaciones de energía solar',
    cat_travel_monitors: 'Monitores de Viaje',
    cat_travel_monitors_desc: 'Configuraciones de doble pantalla',
    cat_mobile_routers: 'Routers Móviles',
    cat_mobile_routers_desc: 'Conectividad 4G/5G en cualquier lugar',
    cat_remote_work: 'Setup Trabajo Remoto',
    cat_remote_work_desc: 'Esenciales de oficina móvil',
    cat_vanlife: 'Tech Vanlife',
    cat_vanlife_desc: 'Tecnología para vida en furgoneta',
    cat_connectivity_tools: 'Conectividad',
    cat_connectivity_tools_desc: 'Mantente conectado en todo el mundo',
    
    // Categories section
    best_tools_title: 'Mejores Herramientas para Nómadas Digitales',
    best_tools_desc: 'Recomendaciones de equipo seleccionado en seis categorías esenciales para trabajo remoto y viajes.',
    
    // Article sections
    the_challenge: 'El Desafío',
    the_solution: 'La Solución',
    related_guides: 'Guías Relacionadas',
    key_features: 'Características Clave para Trabajo Remoto',
    pros_and_cons: 'Pros y Contras',
    who_is_for: 'Para Quién Es',
    best_alternatives: 'Mejores Alternativas en Amazon',
    our_verdict: 'Nuestro Veredicto',
    faqs: 'Preguntas Frecuentes',
    what_to_consider: 'Qué Considerar',
    how_we_tested: 'Cómo Probamos',
    personally_tested: 'Probado Personalmente',
    top_products: 'Productos Destacados',
    in_depth_guides: 'Guías Detalladas',
    all_articles: 'Todos los Artículos',
    buying_guide: 'Guía de Compra',
    
    // Navigation menu items
    nav_workstations: 'Estaciones de Trabajo',
    nav_connectivity: 'Conectividad',
    nav_displays: 'Pantallas',
    nav_power: 'Energía',
    nav_travel_setup: 'Setup de Viaje',
    nav_accessories: 'Accesorios',
    nav_guides: 'Guías',
    
    // Workstation submenu
    nav_temporary_desk: 'Setup de Escritorio Temporal',
    nav_hotel_room: 'Setup de Habitación de Hotel',
    nav_compact_workstations: 'Estaciones Compactas',
    nav_shared_spaces: 'Espacios Compartidos',
    nav_on_the_move: 'Setup en Movimiento',
    nav_dual_monitor: 'Setup Dual Monitor',
    nav_standing_flexible: 'Estaciones de Pie y Flexibles',
    nav_workspace_config: 'Configuraciones de Espacio de Trabajo',
    
    // Language selector
    active: 'Activo',
    
    travel_setup: 'Setup de Viaje',
    
    // Footer
    footer_equipment: 'Equipamiento',
    footer_workstations: 'Estaciones de Trabajo',
    footer_setup_guides: 'Guías de Configuración',
    footer_resources: 'Recursos',
    footer_all_guides: 'Todas las Guías',
    footer_about_us: 'Sobre Nosotros',
    footer_independently_tested: 'Probado Independientemente',
    footer_affiliate_disclosure: 'Divulgación de Afiliados',
    footer_affiliate_text: 'Los precios mostrados son precisos en el momento de escribir. Por favor, verifica los precios actuales en Amazon antes de comprar. Solo recomendamos productos que hemos probado.',
    footer_all_rights: 'Todos los derechos reservados.',
    footer_privacy_policy: 'Política de Privacidad',
    footer_terms: 'Términos de Servicio',
    footer_cookies: 'Cookies',
    footer_description: 'Reseñas de equipos y configuraciones de espacios de trabajo para trabajar productivamente desde cualquier lugar.',
    
    // SEO categories
    seo_laptop_setup: 'Configuración de Laptop',
    seo_temporary_workspaces: 'Espacios Temporales',
    seo_dual_monitor: 'Setup Doble Monitor',
    seo_portable_office: 'Oficina Portátil',
    seo_work_from_hotel: 'Trabajar desde Hotel',
    seo_mobile_connectivity: 'Conectividad Móvil',
  },
  
  it: {
    home: 'Home',
    blog: 'Blog',
    about: 'Chi Siamo',
    contact: 'Contatti',
    categories: 'Categorie',
    
    check_price: 'Vedi Prezzo',
    check_price_amazon: 'Vedi su Amazon',
    view_deal: "Vedi l'Offerta",
    see_offer: 'Scopri Offerta',
    read_more: 'Leggi le Recensioni',
    read_article: 'Leggi Articolo Completo',
    learn_more: 'Scopri di Più',
    coming_soon: 'In Arrivo',
    view_all: 'Vedi Tutto',
    
    // Newsletter
    newsletter: 'Newsletter',
    get_best_gear: 'Ricevi le Migliori Scelte',
    weekly_reviews: 'Recensioni settimanali, offerte e consigli per nomadi digitali. Niente spam.',
    your_name: 'Il tuo nome',
    your_email: 'La tua email',
    privacy_agree: 'Accetto di ricevere email e la Privacy Policy. Puoi disiscriverti in qualsiasi momento.',
    subscribe_now: 'Iscriviti',
    subscribing: 'Iscrizione...',
    join_nomads: 'Unisciti a 5.000+ nomadi digitali',
    youre_in: 'Sei Dentro!',
    check_inbox: 'Controlla la tua casella per la guida di benvenuto.',
    weekly_gear_picks: 'Ricevi Consigli Settimanali',
    
    // Home sections
    stay_connected: 'Resta Connesso Ovunque',
    connectivity_desc: 'Internet affidabile è essenziale per il lavoro remoto. Questi sono gli strumenti che ci tengono online ovunque.',
    mobile_routers_5g: 'Router Mobili 5G/4G',
    signal_boosters: 'Amplificatori di Segnale',
    esim_solutions: 'Soluzioni eSIM',
    starlink_satellite: 'Starlink e Satellite',
    best_mobile_routers: 'Migliori Router Mobili',
    power_your_work: 'Alimenta il Tuo Lavoro Ovunque',
    power_desc: 'Dalle power station portatili ai pannelli solari, resta alimentato in ogni ambiente.',
    power_stations: 'Power Station Portatili',
    solar_charging: 'Ricarica Solare',
    laptop_power_banks: 'Power Bank per Laptop',
    travel_adapters: 'Adattatori da Viaggio',
    best_power_stations: 'Migliori Power Station',
    
    best_overall: 'Migliore in Assoluto',
    best_budget: 'Miglior Rapporto Qualità-Prezzo',
    best_premium: 'Miglior Premium',
    best_compact: 'Più Compatto',
    editors_choice: 'Scelta della Redazione',
    top_pick: 'Top Scelta',
    
    top_rated_products: 'Prodotti Più Votati',
    new_arrivals: 'Nuovi Arrivi',
    new_in_2026: 'NOVITÀ 2026',
    latest_reviews: 'Ultime Recensioni',
    featured_products: 'Prodotti in Evidenza',
    popular_categories: 'Categorie Popolari',
    related_products: 'Prodotti Correlati',
    you_may_also_like: 'Potrebbe Piacerti Anche',
    latest_additions: 'Ultime aggiunte alla nostra collezione di attrezzatura testata.',
    view_all_new: 'Vedi Tutti i Nuovi Prodotti',
    in_depth_guides: 'Guide approfondite e confronti di prodotti.',
    editors_picks: 'Le scelte dei nostri esperti per la migliore attrezzatura del mese.',
    view_all_reviews: 'Vedi Tutte le Recensioni',
    
    portable_power: 'Energia Portatile',
    travel_monitors: 'Monitor da Viaggio',
    mobile_routers: 'Router Mobili',
    solar_panels: 'Pannelli Solari',
    power_banks: 'Power Bank',
    headphones: 'Cuffie',
    keyboards: 'Tastiere',
    webcams: 'Webcam',
    connectivity: 'Connettività',
    workstations: 'Postazioni di Lavoro',
    displays: 'Display',
    power: 'Alimentazione',
    accessories: 'Accessori',
    guides: 'Guide',
    
    affiliate_disclosure: 'In qualità di Affiliato Amazon, riceviamo un guadagno dagli acquisti idonei.',
    price_disclaimer: 'I prezzi possono variare. Ultima verifica',
    
    search: 'Cerca',
    subscribe: 'Iscriviti',
    send: 'Invia',
    cancel: 'Annulla',
    select_language: 'Seleziona Lingua',
    no_products_found: 'Nessun prodotto trovato',
    loading: 'Caricamento...',
    
    buying_guide: 'Guida all\'Acquisto',
    what_to_consider: 'Cosa Considerare',
    faq: 'Domande Frequenti',
    related_articles: 'Articoli Correlati',
    all_articles: 'Tutti gli Articoli',
    
    share: 'Condividi',
    bookmark: 'Salva',
    read_time: 'min di lettura',
    quick_pick: 'Scelta Rapida',
    which_one_right: 'Qual è Quello Giusto per Te?',
    
    hero_title: 'Lavora Ovunque Senza Limiti',
    hero_subtitle: 'Recensioni e guide esperte per l\'attrezzatura che alimenta il tuo stile di vita remoto.',
    hero_independently_tested: 'Testato Indipendentemente',
    hero_readers: '5.000+ lettori',
    hero_rating: '4.9 valutazione',
    hero_best_power_stations: 'Migliori Power Station',
    hero_description: 'Testiamo e raccomandiamo i migliori strumenti tech per lavoro remoto, vita off-grid e nomadi digitali. Recensioni reali da nomadi reali.',
    hero_latest_review: 'Ultima Recensione',
    hero_editorial_title: 'La Tua Guida Affidabile per il Miglior',
    hero_editorial_title_accent: 'Equipaggiamento da Lavoro Remoto',
    hero_editorial_subtitle: 'Recensioni oneste e approfondite di laptop, power station, router e tutto ciò di cui un nomade digitale ha bisogno.',
    hero_cta_reviews: 'Leggi le Ultime Recensioni',
    hero_featured_review: 'Recensione in Evidenza',
    hero_read_review: 'Leggi la Recensione',
    latest_reviews_guides: 'Ultime Recensioni e Guide',
    view_all_articles: 'Vedi Tutti gli Articoli',
    editors_picks_title: 'Scelti dalla Redazione',
    editors_picks_subtitle: 'I nostri prodotti top, testati e recensiti',
    view_all_products: 'Vedi Tutti i Prodotti',
    explore_by_topic: 'Esplora per Argomento',
    explore_by_topic_desc: 'Naviga per categoria — dall\'energia portatile ai setup per lavoro remoto',
    featured_guide_label: 'Guida Approfondita',
    featured_guide_cta: 'Leggi la Guida Completa',

    // Price disclaimer
    price_disclaimer: 'Prezzo accurato al momento della scrittura. Verifica il prezzo attuale su Amazon.',
    
    // Homepage categories
    cat_portable_power: 'Energia Portatile',
    cat_portable_power_desc: 'Power station pronte per il solare',
    cat_travel_monitors: 'Monitor da Viaggio',
    cat_travel_monitors_desc: 'Setup portatili doppio schermo',
    cat_mobile_routers: 'Router Mobili',
    cat_mobile_routers_desc: 'Connettività 4G/5G ovunque',
    cat_remote_work: 'Setup Lavoro Remoto',
    cat_remote_work_desc: 'Essenziali per ufficio mobile',
    cat_vanlife: 'Tech Vanlife',
    cat_vanlife_desc: 'Tecnologia per la vita on the road',
    cat_connectivity_tools: 'Connettività',
    cat_connectivity_tools_desc: 'Resta connesso in tutto il mondo',
    
    // Categories section
    best_tools_title: 'Migliori Strumenti per Nomadi Digitali',
    best_tools_desc: 'Raccomandazioni di attrezzatura selezionata in sei categorie essenziali per lavoro remoto e viaggi.',
    
    // Article sections
    the_challenge: 'La Sfida',
    the_solution: 'La Soluzione',
    related_guides: 'Guide Correlate',
    key_features: 'Funzionalità Chiave per il Lavoro Remoto',
    pros_and_cons: 'Pro e Contro',
    who_is_for: 'Per Chi È Adatto',
    best_alternatives: 'Migliori Alternative su Amazon',
    our_verdict: 'Il Nostro Verdetto',
    faqs: 'Domande Frequenti',
    what_to_consider: 'Cosa Considerare',
    how_we_tested: 'Come Abbiamo Testato',
    
    // Navigation menu items
    nav_workstations: 'Postazioni di Lavoro',
    nav_connectivity: 'Connettività',
    nav_displays: 'Display',
    nav_power: 'Alimentazione',
    nav_travel_setup: 'Setup da Viaggio',
    nav_accessories: 'Accessori',
    nav_guides: 'Guide',
    
    // Workstation submenu
    nav_temporary_desk: 'Setup Scrivania Temporanea',
    nav_hotel_room: 'Setup Camera d\'Hotel',
    nav_compact_workstations: 'Postazioni Compatte',
    nav_shared_spaces: 'Spazi Condivisi',
    nav_on_the_move: 'Setup in Movimento',
    nav_dual_monitor: 'Setup Doppio Monitor',
    nav_standing_flexible: 'Postazioni in Piedi e Flessibili',
    nav_workspace_config: 'Configurazioni Spazio di Lavoro',
    
    // Language selector
    active: 'Attivo',
    
    travel_setup: 'Setup da Viaggio',
    
    // Footer
    footer_equipment: 'Attrezzatura',
    footer_workstations: 'Postazioni di Lavoro',
    footer_setup_guides: 'Guide di Configurazione',
    footer_resources: 'Risorse',
    footer_all_guides: 'Tutte le Guide',
    footer_about_us: 'Chi Siamo',
    footer_independently_tested: 'Testato Indipendentemente',
    footer_affiliate_disclosure: 'Divulgazione Affiliati',
    footer_affiliate_text: 'I prezzi mostrati sono accurati al momento della scrittura. Verifica i prezzi attuali su Amazon prima di acquistare. Raccomandiamo solo prodotti che abbiamo testato.',
    footer_all_rights: 'Tutti i diritti riservati.',
    footer_privacy_policy: 'Privacy Policy',
    footer_terms: 'Termini di Servizio',
    footer_cookies: 'Cookie',
    footer_description: 'Recensioni di attrezzature e configurazioni di spazi di lavoro per lavorare produttivamente da qualsiasi luogo.',
    
    // SEO categories
    seo_laptop_setup: 'Setup Laptop',
    seo_temporary_workspaces: 'Spazi Temporanei',
    seo_dual_monitor: 'Setup Doppio Monitor',
    seo_portable_office: 'Ufficio Portatile',
    seo_work_from_hotel: 'Lavorare dall\'Hotel',
    seo_mobile_connectivity: 'Connettività Mobile',
  },
  
  de: {
    home: 'Startseite',
    blog: 'Blog',
    about: 'Über Uns',
    contact: 'Kontakt',
    categories: 'Kategorien',
    
    check_price: 'Preis Prüfen',
    check_price_amazon: 'Auf Amazon',
    view_deal: 'Angebot Ansehen',
    see_offer: 'Angebot Entdecken',
    read_more: 'Bewertungen Lesen',
    read_article: 'Vollständigen Artikel Lesen',
    learn_more: 'Mehr Erfahren',
    coming_soon: 'Demnächst',
    view_all: 'Alle Anzeigen',
    
    // Newsletter
    newsletter: 'Newsletter',
    get_best_gear: 'Die Besten Auswahlen Erhalten',
    weekly_reviews: 'Wöchentliche Bewertungen, Angebote und Tipps für digitale Nomaden. Kein Spam.',
    your_name: 'Dein Name',
    your_email: 'Deine E-Mail',
    privacy_agree: 'Ich stimme dem Erhalt von E-Mails und der Datenschutzrichtlinie zu. Du kannst dich jederzeit abmelden.',
    subscribe_now: 'Abonnieren',
    subscribing: 'Abonniere...',
    join_nomads: 'Schließe dich 5.000+ digitalen Nomaden an',
    youre_in: 'Du Bist Dabei!',
    check_inbox: 'Überprüfe deinen Posteingang für den Willkommensguide.',
    weekly_gear_picks: 'Wöchentliche Tipps Erhalten',
    
    // Home sections
    stay_connected: 'Überall Verbunden Bleiben',
    connectivity_desc: 'Zuverlässiges Internet ist unverzichtbar für Remote-Arbeit. Dies sind die Tools, die uns überall online halten.',
    mobile_routers_5g: '5G/4G Mobile Router',
    signal_boosters: 'Signalverstärker & Antennen',
    esim_solutions: 'eSIM Lösungen',
    starlink_satellite: 'Starlink & Satellit',
    best_mobile_routers: 'Beste Mobile Router',
    power_your_work: 'Versorge Deine Arbeit Überall',
    power_desc: 'Von tragbaren Powerstations bis zu Solarpanelen, bleibe in jeder Umgebung mit Strom versorgt.',
    power_stations: 'Tragbare Powerstations',
    solar_charging: 'Solarladung',
    laptop_power_banks: 'Laptop Powerbanks',
    travel_adapters: 'Reiseadapter',
    best_power_stations: 'Beste Powerstations',
    
    best_overall: 'Beste Gesamtwahl',
    best_budget: 'Bestes Preis-Leistungs-Verhältnis',
    best_premium: 'Beste Premium-Wahl',
    best_compact: 'Kompakteste',
    editors_choice: 'Empfehlung der Redaktion',
    top_pick: 'Top-Empfehlung',
    
    top_rated_products: 'Bestbewertete Produkte',
    new_arrivals: 'Neuheiten',
    new_in_2026: 'NEU IN 2026',
    latest_reviews: 'Neueste Bewertungen',
    featured_products: 'Empfohlene Produkte',
    popular_categories: 'Beliebte Kategorien',
    related_products: 'Ähnliche Produkte',
    you_may_also_like: 'Das Könnte Dir Auch Gefallen',
    latest_additions: 'Neueste Ergänzungen unserer getesteten Ausrüstungskollektion.',
    view_all_new: 'Alle Neuen Produkte Ansehen',
    in_depth_guides: 'Detaillierte Ratgeber und Produktvergleiche.',
    editors_picks: 'Die Auswahl unserer Redakteure für die beste Ausrüstung diesen Monat.',
    view_all_reviews: 'Alle Bewertungen Ansehen',
    
    portable_power: 'Tragbare Energie',
    travel_monitors: 'Reisemonitore',
    mobile_routers: 'Mobile Router',
    solar_panels: 'Solarpanele',
    power_banks: 'Powerbanks',
    headphones: 'Kopfhörer',
    keyboards: 'Tastaturen',
    webcams: 'Webcams',
    connectivity: 'Konnektivität',
    workstations: 'Arbeitsstationen',
    displays: 'Bildschirme',
    power: 'Stromversorgung',
    accessories: 'Zubehör',
    guides: 'Ratgeber',
    
    affiliate_disclosure: 'Als Amazon-Partner verdienen wir an qualifizierten Käufen.',
    price_disclaimer: 'Preise können variieren. Zuletzt überprüft',
    
    search: 'Suchen',
    subscribe: 'Abonnieren',
    send: 'Senden',
    cancel: 'Abbrechen',
    select_language: 'Sprache Auswählen',
    no_products_found: 'Keine Produkte gefunden',
    loading: 'Laden...',
    
    buying_guide: 'Kaufratgeber',
    what_to_consider: 'Was zu Beachten ist',
    faq: 'Häufige Fragen',
    related_articles: 'Verwandte Artikel',
    all_articles: 'Alle Artikel',
    
    share: 'Teilen',
    bookmark: 'Speichern',
    read_time: 'Min. Lesezeit',
    quick_pick: 'Schnelle Wahl',
    which_one_right: 'Welches ist das Richtige für Dich?',
    
    hero_title: 'Arbeite Von Überall Ohne Grenzen',
    hero_subtitle: 'Expertenrezensionen und Ratgeber für die Ausrüstung, die deinen Remote-Lebensstil antreibt.',
    hero_independently_tested: 'Unabhängig Getestet',
    hero_readers: '5.000+ Leser',
    hero_rating: '4.9 Bewertung',
    hero_best_power_stations: 'Beste Powerstations',
    hero_description: 'Wir testen und empfehlen die besten Tech-Tools für Remote-Arbeit, Off-Grid-Leben und digitales Nomadentum. Echte Bewertungen von echten Nomaden.',
    hero_latest_review: 'Neueste Bewertung',
    hero_editorial_title: 'Dein Vertrauenswürdiger Guide für die Beste',
    hero_editorial_title_accent: 'Remote-Arbeitsausstattung',
    hero_editorial_subtitle: 'Ehrliche, ausführliche Reviews von Laptops, Powerstations, Routern und allem, was ein digitaler Nomade braucht.',
    hero_cta_reviews: 'Unsere Neuesten Reviews Lesen',
    hero_featured_review: 'Empfohlener Review',
    hero_read_review: 'Review Lesen',
    latest_reviews_guides: 'Neueste Reviews & Ratgeber',
    view_all_articles: 'Alle Artikel Ansehen',
    editors_picks_title: 'Redaktionsempfehlungen',
    editors_picks_subtitle: 'Unsere bestbewertete Ausrüstung, getestet und bewertet',
    view_all_products: 'Alle Produkte Ansehen',
    explore_by_topic: 'Nach Thema Entdecken',
    explore_by_topic_desc: 'Durchsuche unsere Kategorien — von tragbarer Energie bis zu Remote-Arbeitssetups',
    featured_guide_label: 'Ausführlicher Ratgeber',
    featured_guide_cta: 'Den Vollständigen Ratgeber Lesen',

    // Price disclaimer
    price_disclaimer: 'Preis korrekt zum Zeitpunkt des Schreibens. Aktuellen Preis auf Amazon prüfen.',
    
    // Homepage categories
    cat_portable_power: 'Tragbare Energie',
    cat_portable_power_desc: 'Solar-fähige Powerstations',
    cat_travel_monitors: 'Reisemonitore',
    cat_travel_monitors_desc: 'Tragbare Dual-Screen-Setups',
    cat_mobile_routers: 'Mobile Router',
    cat_mobile_routers_desc: '4G/5G-Konnektivität überall',
    cat_remote_work: 'Remote-Arbeits-Setup',
    cat_remote_work_desc: 'Mobile Büro-Essentials',
    cat_vanlife: 'Vanlife Tech',
    cat_vanlife_desc: 'Technik für das Leben unterwegs',
    cat_connectivity_tools: 'Konnektivität',
    cat_connectivity_tools_desc: 'Weltweit verbunden bleiben',
    
    // Categories section
    best_tools_title: 'Beste Werkzeuge für Digitale Nomaden',
    best_tools_desc: 'Kuratierte Ausrüstungsempfehlungen in sechs wesentlichen Kategorien für Remote-Arbeit und Reisen.',
    
    // Article sections
    the_challenge: 'Die Herausforderung',
    the_solution: 'Die Lösung',
    related_guides: 'Verwandte Anleitungen',
    key_features: 'Schlüsselfunktionen für Remote-Arbeit',
    pros_and_cons: 'Vor- und Nachteile',
    who_is_for: 'Für Wen Es Geeignet Ist',
    best_alternatives: 'Beste Alternativen auf Amazon',
    our_verdict: 'Unser Urteil',
    faqs: 'Häufig Gestellte Fragen',
    what_to_consider: 'Was zu Beachten Ist',
    how_we_tested: 'Wie Wir Getestet Haben',
    
    // Navigation menu items
    nav_workstations: 'Arbeitsstationen',
    nav_connectivity: 'Konnektivität',
    nav_displays: 'Bildschirme',
    nav_power: 'Stromversorgung',
    nav_travel_setup: 'Reise-Setup',
    nav_accessories: 'Zubehör',
    nav_guides: 'Ratgeber',
    
    // Workstation submenu
    nav_temporary_desk: 'Temporäres Schreibtisch-Setup',
    nav_hotel_room: 'Hotelzimmer-Setup',
    nav_compact_workstations: 'Kompakte Arbeitsstationen',
    nav_shared_spaces: 'Gemeinschaftsräume',
    nav_on_the_move: 'Mobiles Setup',
    nav_dual_monitor: 'Dual-Monitor-Setup',
    nav_standing_flexible: 'Steh- & Flexible Arbeitsstationen',
    nav_workspace_config: 'Arbeitsplatz-Konfigurationen',
    
    // Language selector
    active: 'Aktiv',
    
    travel_setup: 'Reise-Setup',
    
    // Footer
    footer_equipment: 'Ausrüstung',
    footer_workstations: 'Arbeitsstationen',
    footer_setup_guides: 'Setup-Anleitungen',
    footer_resources: 'Ressourcen',
    footer_all_guides: 'Alle Anleitungen',
    footer_about_us: 'Über Uns',
    footer_independently_tested: 'Unabhängig Getestet',
    footer_affiliate_disclosure: 'Affiliate-Offenlegung',
    footer_affiliate_text: 'Die angezeigten Preise sind zum Zeitpunkt des Schreibens korrekt. Bitte überprüfe die aktuellen Preise auf Amazon vor dem Kauf. Wir empfehlen nur Produkte, die wir getestet haben.',
    footer_all_rights: 'Alle Rechte vorbehalten.',
    footer_privacy_policy: 'Datenschutz',
    footer_terms: 'Nutzungsbedingungen',
    footer_cookies: 'Cookies',
    footer_description: 'Ausrüstungsbewertungen und Arbeitsplatz-Konfigurationen für produktives Arbeiten von jedem Ort.',
    
    // SEO categories
    seo_laptop_setup: 'Laptop-Setup',
    seo_temporary_workspaces: 'Temporäre Arbeitsplätze',
    seo_dual_monitor: 'Dual-Monitor-Setup',
    seo_portable_office: 'Mobiles Büro',
    seo_work_from_hotel: 'Arbeiten im Hotel',
    seo_mobile_connectivity: 'Mobile Konnektivität',
  },
  
  fr: {
    home: 'Accueil',
    blog: 'Blog',
    about: 'À Propos',
    contact: 'Contact',
    categories: 'Catégories',
    
    check_price: 'Voir le Prix',
    check_price_amazon: 'Sur Amazon',
    view_deal: "Voir l'Offre",
    see_offer: 'Découvrir Offre',
    read_more: 'Lire les Avis',
    read_article: "Lire l'Article Complet",
    learn_more: 'En Savoir Plus',
    coming_soon: 'Bientôt Disponible',
    view_all: 'Voir Tout',
    
    // Newsletter
    newsletter: 'Newsletter',
    get_best_gear: 'Recevez les Meilleures Sélections',
    weekly_reviews: 'Avis hebdomadaires, offres et conseils pour nomades numériques. Pas de spam.',
    your_name: 'Votre nom',
    your_email: 'Votre email',
    privacy_agree: "J'accepte de recevoir des emails et la Politique de Confidentialité. Vous pouvez vous désabonner à tout moment.",
    subscribe_now: "S'abonner",
    subscribing: 'Abonnement...',
    join_nomads: 'Rejoignez 5 000+ nomades numériques',
    youre_in: 'Vous Êtes Inscrit!',
    check_inbox: 'Vérifiez votre boîte mail pour le guide de bienvenue.',
    weekly_gear_picks: 'Recevez des Conseils Hebdomadaires',
    
    // Home sections
    stay_connected: 'Restez Connecté Partout',
    connectivity_desc: "Un internet fiable est essentiel pour le travail à distance. Voici les outils qui nous gardent en ligne partout.",
    mobile_routers_5g: 'Routeurs Mobiles 5G/4G',
    signal_boosters: 'Amplificateurs de Signal',
    esim_solutions: 'Solutions eSIM',
    starlink_satellite: 'Starlink et Satellite',
    best_mobile_routers: 'Meilleurs Routeurs Mobiles',
    power_your_work: 'Alimentez Votre Travail Partout',
    power_desc: 'Des stations d\'énergie portables aux panneaux solaires, restez alimenté dans tout environnement.',
    power_stations: 'Stations d\'Énergie Portables',
    solar_charging: 'Recharge Solaire',
    laptop_power_banks: 'Batteries Externes Laptop',
    travel_adapters: 'Adaptateurs de Voyage',
    best_power_stations: 'Meilleures Stations d\'Énergie',
    
    best_overall: 'Meilleur Choix Global',
    best_budget: 'Meilleur Rapport Qualité-Prix',
    best_premium: 'Meilleur Premium',
    best_compact: 'Plus Compact',
    editors_choice: 'Choix de la Rédaction',
    top_pick: 'Coup de Cœur',
    
    top_rated_products: 'Produits Les Mieux Notés',
    new_arrivals: 'Nouveautés',
    new_in_2026: 'NOUVEAU EN 2026',
    latest_reviews: 'Derniers Avis',
    featured_products: 'Produits Vedettes',
    popular_categories: 'Catégories Populaires',
    related_products: 'Produits Similaires',
    you_may_also_like: 'Vous Aimerez Aussi',
    latest_additions: 'Dernières additions à notre collection d\'équipements testés.',
    view_all_new: 'Voir Tous les Nouveaux Produits',
    in_depth_guides: 'Guides approfondis et comparaisons de produits.',
    editors_picks: 'Les choix de nos rédacteurs pour les meilleurs équipements du mois.',
    view_all_reviews: 'Voir Tous les Avis',
    
    portable_power: 'Énergie Portable',
    travel_monitors: 'Moniteurs de Voyage',
    mobile_routers: 'Routeurs Mobiles',
    solar_panels: 'Panneaux Solaires',
    power_banks: 'Batteries Externes',
    headphones: 'Casques',
    keyboards: 'Claviers',
    webcams: 'Webcams',
    connectivity: 'Connectivité',
    workstations: 'Postes de Travail',
    displays: 'Écrans',
    power: 'Alimentation',
    accessories: 'Accessoires',
    guides: 'Guides',
    
    affiliate_disclosure: 'En tant que Partenaire Amazon, nous gagnons des commissions sur les achats éligibles.',
    price_disclaimer: 'Les prix peuvent varier. Dernière vérification',
    
    search: 'Rechercher',
    subscribe: "S'Abonner",
    send: 'Envoyer',
    cancel: 'Annuler',
    select_language: 'Choisir la Langue',
    no_products_found: 'Aucun produit trouvé',
    loading: 'Chargement...',
    
    buying_guide: "Guide d'Achat",
    what_to_consider: 'Ce qu\'il Faut Considérer',
    faq: 'Questions Fréquentes',
    related_articles: 'Articles Connexes',
    all_articles: 'Tous les Articles',
    
    share: 'Partager',
    bookmark: 'Sauvegarder',
    read_time: 'min de lecture',
    quick_pick: 'Choix Rapide',
    which_one_right: 'Lequel est Fait pour Vous?',
    
    hero_title: 'Travaillez De N\'importe Où Sans Limites',
    hero_subtitle: 'Avis d\'experts et guides pour l\'équipement qui alimente votre style de vie nomade.',
    hero_independently_tested: 'Testé Indépendamment',
    hero_readers: '5 000+ lecteurs',
    hero_rating: '4.9 évaluation',
    hero_best_power_stations: 'Meilleures Stations d\'Énergie',
    hero_description: 'Nous testons et recommandons les meilleurs outils tech pour le travail à distance, la vie off-grid et les nomades numériques. Avis réels par de vrais nomades.',
    hero_latest_review: 'Dernier Avis',
    hero_editorial_title: 'Votre Guide de Confiance pour le Meilleur',
    hero_editorial_title_accent: 'Équipement de Travail à Distance',
    hero_editorial_subtitle: 'Avis honnêtes et approfondis sur les laptops, stations d\'énergie, routeurs et tout ce dont un nomade numérique a besoin.',
    hero_cta_reviews: 'Lire Nos Derniers Avis',
    hero_featured_review: 'Avis en Vedette',
    hero_read_review: 'Lire l\'Avis',
    latest_reviews_guides: 'Derniers Avis & Guides',
    view_all_articles: 'Voir Tous les Articles',
    editors_picks_title: 'Sélection de la Rédaction',
    editors_picks_subtitle: 'Notre équipement le mieux noté, testé et évalué',
    view_all_products: 'Voir Tous les Produits',
    explore_by_topic: 'Explorer par Thème',
    explore_by_topic_desc: 'Parcourez nos catégories — de l\'énergie portable aux setups de travail à distance',
    featured_guide_label: 'Guide Approfondi',
    featured_guide_cta: 'Lire le Guide Complet',

    // Price disclaimer
    price_disclaimer: 'Prix exact au moment de la rédaction. Vérifiez le prix actuel sur Amazon.',
    
    // Homepage categories
    cat_portable_power: 'Énergie Portable',
    cat_portable_power_desc: 'Stations d\'énergie solaires',
    cat_travel_monitors: 'Moniteurs de Voyage',
    cat_travel_monitors_desc: 'Configurations double écran portables',
    cat_mobile_routers: 'Routeurs Mobiles',
    cat_mobile_routers_desc: 'Connectivité 4G/5G partout',
    cat_remote_work: 'Setup Travail à Distance',
    cat_remote_work_desc: 'Essentiels bureau mobile',
    cat_vanlife: 'Tech Vanlife',
    cat_vanlife_desc: 'Technologie pour la vie nomade',
    cat_connectivity_tools: 'Connectivité',
    cat_connectivity_tools_desc: 'Restez connecté dans le monde entier',
    
    // Categories section
    best_tools_title: 'Meilleurs Outils pour Nomades Numériques',
    best_tools_desc: 'Recommandations d\'équipement sélectionné dans six catégories essentielles pour le travail à distance et les voyages.',
    
    // Article sections
    the_challenge: 'Le Défi',
    the_solution: 'La Solution',
    related_guides: 'Guides Connexes',
    key_features: 'Fonctionnalités Clés pour le Travail à Distance',
    pros_and_cons: 'Avantages et Inconvénients',
    who_is_for: 'Pour Qui C\'Est Fait',
    best_alternatives: 'Meilleures Alternatives sur Amazon',
    our_verdict: 'Notre Verdict',
    faqs: 'Questions Fréquentes',
    what_to_consider: 'À Considérer',
    how_we_tested: 'Comment Nous Avons Testé',
    
    // Navigation menu items
    nav_workstations: 'Postes de Travail',
    nav_connectivity: 'Connectivité',
    nav_displays: 'Écrans',
    nav_power: 'Alimentation',
    nav_travel_setup: 'Setup de Voyage',
    nav_accessories: 'Accessoires',
    nav_guides: 'Guides',
    
    // Workstation submenu
    nav_temporary_desk: 'Setup Bureau Temporaire',
    nav_hotel_room: 'Setup Chambre d\'Hôtel',
    nav_compact_workstations: 'Postes Compacts',
    nav_shared_spaces: 'Espaces Partagés',
    nav_on_the_move: 'Setup en Déplacement',
    nav_dual_monitor: 'Setup Double Écran',
    nav_standing_flexible: 'Postes Debout & Flexibles',
    nav_workspace_config: 'Configurations d\'Espace de Travail',
    
    // Language selector
    active: 'Actif',
    
    travel_setup: 'Setup de Voyage',
    
    // Footer
    footer_equipment: 'Équipement',
    footer_workstations: 'Postes de Travail',
    footer_setup_guides: 'Guides de Configuration',
    footer_resources: 'Ressources',
    footer_all_guides: 'Tous les Guides',
    footer_about_us: 'À Propos',
    footer_independently_tested: 'Testé Indépendamment',
    footer_affiliate_disclosure: 'Divulgation d\'Affiliation',
    footer_affiliate_text: 'Les prix affichés sont exacts au moment de la rédaction. Veuillez vérifier les prix actuels sur Amazon avant d\'acheter. Nous ne recommandons que des produits que nous avons testés.',
    footer_all_rights: 'Tous droits réservés.',
    footer_privacy_policy: 'Politique de Confidentialité',
    footer_terms: 'Conditions d\'Utilisation',
    footer_cookies: 'Cookies',
    footer_description: 'Avis sur les équipements et configurations d\'espaces de travail pour travailler productivement depuis n\'importe où.',
    
    // SEO categories
    seo_laptop_setup: 'Setup Laptop',
    seo_temporary_workspaces: 'Espaces Temporaires',
    seo_dual_monitor: 'Setup Double Écran',
    seo_portable_office: 'Bureau Portable',
    seo_work_from_hotel: 'Travailler depuis l\'Hôtel',
    seo_mobile_connectivity: 'Connectivité Mobile',
  },
};

// Language Context
const LanguageContext = createContext(null);

// Storage key
const LANG_STORAGE_KEY = 'nomadlivinglab_language';

export const LanguageProvider = ({ children }) => {
  // Initialize from localStorage or browser preference
  const [language, setLanguageState] = useState(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(LANG_STORAGE_KEY);
      if (stored && SUPPORTED_LANGUAGES.some(l => l.code === stored)) {
        return stored;
      }
      
      // Check browser language
      const browserLang = navigator.language?.split('-')[0];
      if (browserLang && SUPPORTED_LANGUAGES.some(l => l.code === browserLang)) {
        return browserLang;
      }
    }
    return 'en';
  });

  // Persist language choice
  const setLanguage = useCallback((lang) => {
    if (SUPPORTED_LANGUAGES.some(l => l.code === lang)) {
      setLanguageState(lang);
      localStorage.setItem(LANG_STORAGE_KEY, lang);
    }
  }, []);

  // Translation function
  const t = useCallback((key) => {
    return TRANSLATIONS[language]?.[key] || TRANSLATIONS.en[key] || key;
  }, [language]);

  // Get Amazon domain for current language
  const getAmazonDomain = useCallback(() => {
    return AMAZON_DOMAINS[language] || AMAZON_DOMAINS.en;
  }, [language]);

  // Get geo-targeted affiliate link
  const getAffiliateLink = useCallback((product) => {
    if (!product) return '';
    
    // Check for language-specific link
    if (product.affiliate_links && product.affiliate_links[language]) {
      return product.affiliate_links[language];
    }
    
    // Fall back to default affiliate link
    return product.affiliate_link || '';
  }, [language]);

  // Update document lang attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = {
    language,
    setLanguage,
    t,
    getAmazonDomain,
    getAffiliateLink,
    supportedLanguages: SUPPORTED_LANGUAGES,
    translations: TRANSLATIONS[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
